import React, { useState, useEffect, useRef, useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import {
  Ticket, Plus, Trash2, ArrowUp, ArrowDown, RefreshCw, Save,
  Type, QrCode, Image as ImageIcon, Minus as MinusIcon, MoveVertical, Palette, GripVertical,
  AlignLeft, AlignCenter, AlignRight, Bold, Italic,
  Copy, Star, Printer, X, Edit3, AlertTriangle, FileText, Loader2,
} from "lucide-react";
import { openPdf } from "@/lib/pdf";

// ---- Block-Typ Metadata --------------------------------------------------
const BLOCK_TYPES = [
  { v: "logo",    l: "Logo",       icon: ImageIcon },
  { v: "text",    l: "Text",       icon: Type },
  { v: "qr",      l: "QR-Code",    icon: QrCode },
  { v: "divider", l: "Trennlinie", icon: MinusIcon },
  { v: "spacer",  l: "Abstand",    icon: MoveVertical },
];

const SIZE_PX = { xs: 10, sm: 12, md: 14, lg: 20, xl: 28 };
const SIZE_LABELS = [
  { v: "xs", l: "XS · 10 px" }, { v: "sm", l: "S · 12 px" },
  { v: "md", l: "M · 14 px" },  { v: "lg", l: "L · 20 px" },
  { v: "xl", l: "XL · 28 px" },
];

const SAMPLE_DATA = {
  cinema:   "THE MOVIE HOUSE", slogan: "More Than Watching Movies.",
  movie:    "Interstellar", date: "04.08.2026", time: "20:00", hall: "Saal 1",
  row:      "C", seat: "07",
  guest:    "Erika Musterfrau", price: "8,00 €", ticket_no: "MH-000247",
};

const PLACEHOLDERS = ["cinema", "slogan", "movie", "date", "time", "hall", "row", "seat", "guest", "price", "ticket_no"];

function interpolate(template, data) {
  return String(template || "").replace(/\{(\w+)\}/g, (_, k) => data[k] ?? `{${k}}`);
}

function BlockPreview({ block, design, qrValue }) {
  const alignCls = { left: "text-left", center: "text-center", right: "text-right" }[block.align || "center"] || "text-center";
  const style = {
    color: block.color || design.foreground || "#0A0A0A",
    fontSize: SIZE_PX[block.size || "sm"], fontWeight: block.bold ? 700 : 400,
    fontStyle: block.italic ? "italic" : "normal",
    paddingTop: block.padding_top || 0, paddingBottom: block.padding_bottom || 0, lineHeight: 1.25,
  };
  if (block.type === "text") {
    return <div className={alignCls} style={style}>{interpolate(block.content, SAMPLE_DATA) || <span className="opacity-30">(leer)</span>}</div>;
  }
  if (block.type === "logo") {
    const url = design.logo_url; const maxW = design.logo_max_width || 180;
    if (!url) return <div className={alignCls + " opacity-40"} style={{ ...style, fontStyle: "italic", fontSize: 10 }}>
      [Logo – URL im Panel &bdquo;Logo&ldquo; setzen]
    </div>;
    return <div className={alignCls} style={{ padding: `${block.padding_top || 0}px 0 ${block.padding_bottom || 0}px` }}>
      <img src={url} alt="Logo" style={{ maxWidth: maxW, height: "auto", display: block.align === "center" ? "inline-block" : "block", margin: block.align === "center" ? "0 auto" : "0" }} />
    </div>;
  }
  if (block.type === "qr") {
    const size = design.qr_size || 160;
    return <div className={alignCls} style={{ padding: `${block.padding_top || 0}px 0 ${block.padding_bottom || 0}px` }}>
      <div style={{ display: block.align === "center" ? "inline-block" : "block", margin: block.align === "center" ? "0 auto" : "0", background: "#FFFFFF", padding: 6 }}>
        <QRCodeCanvas value={qrValue} size={size} bgColor="#FFFFFF" fgColor={design.foreground || "#0A0A0A"} />
      </div>
    </div>;
  }
  if (block.type === "divider") {
    return <div style={{ padding: `${block.padding_top || 0}px 0 ${block.padding_bottom || 0}px` }}>
      <div style={{ borderTop: `1px dashed ${block.color || design.foreground || "#0A0A0A"}`, opacity: 0.6 }} />
    </div>;
  }
  if (block.type === "spacer") return <div style={{ height: (block.padding_top || 0) + (block.padding_bottom || 12) }} />;
  return null;
}

export default function TicketDesigner() {
  const [templates, setTemplates] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [design, setDesign] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [testPrintOpen, setTestPrintOpen] = useState(false);
  const [manualPrintOpen, setManualPrintOpen] = useState(false);
  const dragIdRef = useRef(null);

  const loadTemplates = async (keepId) => {
    try {
      const { data } = await api.get("/admin/ticket-templates");
      setTemplates(data);
      const nextId = keepId && data.find((t) => t.id === keepId)
        ? keepId
        : (data.find((t) => t.is_default)?.id || data[0]?.id);
      setActiveId(nextId);
    } catch (e) { toast.error("Vorlagen konnten nicht geladen werden"); }
  };
  useEffect(() => { loadTemplates(); }, []);

  // Sync `design` state with active template
  useEffect(() => {
    if (!activeId) { setDesign(null); return; }
    const t = templates.find((x) => x.id === activeId);
    if (t) {
      // Deep clone for safe editing
      setDesign(JSON.parse(JSON.stringify(t)));
      setSelectedId(t.blocks?.[0]?.id || null);
    }
  }, [activeId, templates]);

  const activeTmpl = useMemo(() => templates.find((t) => t.id === activeId) || null, [templates, activeId]);
  const selected = design?.blocks?.find((b) => b.id === selectedId) || null;
  const isDirty = useMemo(() => {
    if (!design || !activeTmpl) return false;
    return JSON.stringify({ ...design, _id: undefined }) !== JSON.stringify({ ...activeTmpl, _id: undefined });
  }, [design, activeTmpl]);

  // ---- design mutations ------------------------------------------------
  const updateDesign = (patch) => setDesign((d) => ({ ...d, ...patch }));
  const updateBlock = (id, patch) => setDesign((d) => ({ ...d, blocks: d.blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)) }));
  const addBlock = (type) => {
    const id = `b${Date.now()}${Math.floor(Math.random() * 999)}`;
    const defaults = {
      logo: { padding_top: 0, padding_bottom: 8 },
      text: { size: "sm", align: "center", content: "Neuer Text", padding_bottom: 4 },
      qr: { padding_top: 4, padding_bottom: 6 },
      divider: { padding_top: 4, padding_bottom: 4 },
      spacer: { padding_top: 0, padding_bottom: 12 },
    };
    const blk = { id, type, align: "center", ...defaults[type] };
    setDesign((d) => ({ ...d, blocks: [...d.blocks, blk] }));
    setSelectedId(id);
  };
  const removeBlock = (id) => {
    setDesign((d) => ({ ...d, blocks: d.blocks.filter((b) => b.id !== id) }));
    if (selectedId === id) setSelectedId(null);
  };
  const move = (id, dir) => setDesign((d) => {
    const idx = d.blocks.findIndex((b) => b.id === id);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= d.blocks.length) return d;
    const arr = [...d.blocks];
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    return { ...d, blocks: arr };
  });
  const insertPlaceholder = (ph) => {
    if (!selected || selected.type !== "text") return;
    updateBlock(selected.id, { content: `${selected.content || ""}{${ph}}` });
  };
  const onDragStart = (id) => { dragIdRef.current = id; };
  const onDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const onDragLeave = () => setDragOverIdx(null);
  const onDrop = (targetIdx) => {
    const dragId = dragIdRef.current; dragIdRef.current = null; setDragOverIdx(null);
    if (!dragId) return;
    setDesign((d) => {
      const from = d.blocks.findIndex((b) => b.id === dragId);
      if (from < 0 || from === targetIdx) return d;
      const arr = [...d.blocks];
      const [moved] = arr.splice(from, 1);
      arr.splice(from < targetIdx ? targetIdx - 1 : targetIdx, 0, moved);
      return { ...d, blocks: arr };
    });
  };

  // ---- template CRUD ---------------------------------------------------
  const saveActive = async () => {
    if (!design) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/ticket-templates/${design.id}`, design);
      toast.success(`&bdquo;${data.name}&ldquo; gespeichert`);
      await loadTemplates(design.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  const createNew = async () => {
    const name = window.prompt("Name der neuen Vorlage:", "VIP Night");
    if (!name?.trim()) return;
    try {
      const src = design || {};
      const { data } = await api.post("/admin/ticket-templates", {
        ...src, id: undefined, _id: undefined, name: name.trim(), is_default: false,
        blocks: src.blocks || [],
      });
      toast.success(`&bdquo;${data.name}&ldquo; angelegt`);
      await loadTemplates(data.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler"); }
  };

  const duplicateActive = async () => {
    if (!activeId) return;
    try {
      const { data } = await api.post(`/admin/ticket-templates/${activeId}/duplicate`);
      toast.success(`Kopie &bdquo;${data.name}&ldquo; angelegt`);
      await loadTemplates(data.id);
    } catch (e) { toast.error("Duplizieren fehlgeschlagen"); }
  };

  const deleteActive = async () => {
    if (!activeTmpl) return;
    if (!window.confirm(`Vorlage &bdquo;${activeTmpl.name}&ldquo; wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/ticket-templates/${activeId}`);
      toast.success("Gelöscht");
      await loadTemplates();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler"); }
  };

  const setAsDefault = async () => {
    if (!activeId) return;
    try {
      await api.post(`/admin/ticket-templates/${activeId}/set-default`);
      toast.success("Als Standard-Vorlage gesetzt");
      await loadTemplates(activeId);
    } catch (e) { toast.error("Fehler"); }
  };

  const rename = async (newName) => {
    if (!design) return;
    if (!newName?.trim()) return toast.error("Name darf nicht leer sein");
    setDesign((d) => ({ ...d, name: newName.trim() }));
    setRenameOpen(false);
  };

  if (!design) {
    return <div className="text-[#575148] text-center py-20" data-testid="td-loading">Lade Ticket-Vorlagen …</div>;
  }

  return (
    <div className="space-y-6" data-testid="ticket-designer-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Ticket size={22} className="text-[#D4AF37]" />
          <div>
            <h1 className="font-heading text-3xl">Ticket-Vorlagen · Epson TM-T88VII</h1>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398] mt-1">Mehrere Layouts pflegen · pro Vorstellung wählen · direkt drucken</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {design.type !== "manual_fallback" && (
            <>
              <button onClick={() => openPdf(`/admin/ticket-templates/${design.id}/sample-preview.pdf`)}
                data-testid="td-sample-preview"
                title="Rendert das aktuelle Design mit echtem Film (Interstellar oder erstem verfügbaren)"
                className="inline-flex items-center gap-2 px-4 py-2 border border-[#EEDC82]/40 text-[#EEDC82] rounded text-[11px] uppercase tracking-widest hover:bg-[#EEDC82]/10">
                <FileText size={12} /> Vorschau mit echtem Film
              </button>
              <button onClick={async () => {
                  try {
                    await api.get(`/admin/ticket-templates/${design.id}/sample-preview.pdf?print_office=true`);
                    toast.success("Muster-Ticket an Bürodrucker gesendet");
                  } catch (e) {
                    toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bürodrucker nicht erreichbar");
                  }
                }}
                data-testid="td-sample-print"
                title="Muster-Ticket direkt an den Bürodrucker senden"
                className="inline-flex items-center gap-2 px-4 py-2 border border-[#8FBC8F]/40 text-[#8FBC8F] rounded text-[11px] uppercase tracking-widest hover:bg-[#8FBC8F]/10">
                <Printer size={12} /> Muster drucken
              </button>
            </>
          )}
          {design.type === "manual_fallback" && (
            <button onClick={() => setManualPrintOpen(true)} data-testid="td-manual-blanks-open"
              className="inline-flex items-center gap-2 px-4 py-2 border border-[#EE9CA1]/60 text-[#EE9CA1] rounded text-[11px] uppercase tracking-widest hover:bg-[#EE9CA1]/10">
              <AlertTriangle size={12} /> Notfall-Designer · Drucken
            </button>
          )}
          <button onClick={() => setTestPrintOpen(true)} data-testid="td-test-print-open"
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#2A2A2A] rounded text-[11px] uppercase tracking-widest hover:border-[#D4AF37]">
            <Printer size={12} /> Testdruck an Epson
          </button>
          <button onClick={saveActive} disabled={busy || !isDirty} data-testid="td-save"
            className={`inline-flex items-center gap-2 px-5 py-2 rounded text-[11px] uppercase tracking-widest disabled:opacity-40 ${isDirty ? "bg-[#D4AF37] hover:bg-[#EEDC82] text-black" : "border border-[#2A2A2A] text-[#575148]"}`}>
            <Save size={12} /> {busy ? "…" : isDirty ? "Änderungen speichern" : "Gespeichert"}
          </button>
        </div>
      </div>

      {/* Template-Picker */}
      <div className="card-lux p-3">
        <div className="flex items-center gap-2 flex-wrap" data-testid="td-template-picker">
          {templates.map((t) => {
            const isActive = t.id === activeId;
            const isManual = t.type === "manual_fallback";
            return (
              <button key={t.id} onClick={() => {
                if (isDirty && !window.confirm("Ungespeicherte Änderungen verwerfen und Vorlage wechseln?")) return;
                setActiveId(t.id);
              }}
                data-testid={`td-tab-${t.id}`}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded border text-sm transitions-safe ${
                  isActive
                    ? (isManual ? "border-[#EE9CA1] bg-[#EE9CA1]/[0.08] text-[#EE9CA1]" : "border-[#D4AF37] bg-[#D4AF37]/[0.08] text-[#EEDC82]")
                    : "border-[#2A2A2A] text-[#A8A398] hover:border-[#D4AF37]/60"
                }`}>
                {t.is_default && <Star size={12} className="text-[#D4AF37]" fill="#D4AF37" />}
                {isManual && <AlertTriangle size={12} className="text-[#EE9CA1]" />}
                <span>{t.name}</span>
              </button>
            );
          })}
          <div className="mx-2 h-6 w-px bg-[#1F1F1F]" />
          <button onClick={createNew} data-testid="td-new" className="inline-flex items-center gap-1 px-3 py-2 text-[10px] uppercase tracking-widest border border-[#D4AF37]/40 text-[#D4AF37] rounded hover:bg-[#D4AF37]/10">
            <Plus size={12} /> Neue Vorlage
          </button>
          <button onClick={duplicateActive} data-testid="td-duplicate" className="inline-flex items-center gap-1 px-3 py-2 text-[10px] uppercase tracking-widest border border-[#2A2A2A] text-[#A8A398] rounded hover:border-[#D4AF37] hover:text-[#D4AF37]">
            <Copy size={12} /> Duplizieren
          </button>
          <button onClick={() => setRenameOpen(true)} data-testid="td-rename" className="inline-flex items-center gap-1 px-3 py-2 text-[10px] uppercase tracking-widest border border-[#2A2A2A] text-[#A8A398] rounded hover:border-[#D4AF37] hover:text-[#D4AF37]">
            <Edit3 size={12} /> Umbenennen
          </button>
          {!activeTmpl?.is_default && (
            <button onClick={setAsDefault} data-testid="td-set-default" className="inline-flex items-center gap-1 px-3 py-2 text-[10px] uppercase tracking-widest border border-[#8B7328] text-[#EEDC82] rounded hover:bg-[#D4AF37]/10">
              <Star size={12} /> Als Standard
            </button>
          )}
          <button onClick={deleteActive} disabled={templates.length <= 1} data-testid="td-delete"
            className="inline-flex items-center gap-1 px-3 py-2 text-[10px] uppercase tracking-widest border border-[#7A1921]/40 text-[#EE9CA1] rounded hover:bg-[#7A1921]/20 disabled:opacity-30 disabled:cursor-not-allowed">
            <Trash2 size={12} /> Löschen
          </button>
        </div>
        <div className="mt-2 pt-2 border-t border-[#1A1A1A] flex items-center gap-3 text-[10px] uppercase tracking-widest text-[#575148]">
          <span>Aktive Vorlage:</span>
          <span className="text-[#EEDC82]">{design.name || "(unbenannt)"}</span>
          {activeTmpl?.is_default && <span className="text-[#D4AF37] flex items-center gap-1"><Star size={9} fill="#D4AF37" /> Standard</span>}
          {isDirty && <span className="text-[#EE9CA1]">· Änderungen nicht gespeichert</span>}
        </div>
      </div>

      <div className="grid xl:grid-cols-[320px_1fr_360px] gap-5">
        {/* ---- Spalte 1 ---- */}
        <div className="space-y-4">
          <div className="card-lux p-4">
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-3 flex items-center gap-2"><Palette size={12} /> Papier &amp; Farben</div>
            <div className="space-y-3 text-sm">
              <RangeInput label="Papierbreite (mm)" min={50} max={210} step={2} value={design.paper_width_mm} onChange={(v) => updateDesign({ paper_width_mm: v })} testid="td-paper-width" />
              <div className="text-[10px] text-[#575148]">Epson TM-T88VII → 80 mm empfohlen</div>
              <ColorInput label="Vordergrund (Text)" value={design.foreground} onChange={(v) => updateDesign({ foreground: v })} testid="td-fg" />
              <ColorInput label="Hintergrund (Papier)" value={design.background} onChange={(v) => updateDesign({ background: v })} testid="td-bg" />
              <ColorInput label="Akzentfarbe" value={design.accent_color} onChange={(v) => updateDesign({ accent_color: v })} testid="td-accent" />
              <label className="flex items-center gap-2 cursor-pointer text-xs">
                <input type="checkbox" checked={!!design.show_border} data-testid="td-border" onChange={(e) => updateDesign({ show_border: e.target.checked })} className="accent-[#D4AF37]" />
                Rahmen um Bon anzeigen (nur Vorschau)
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-xs" data-testid="td-backdrop-toggle-wrap">
                <input type="checkbox" checked={!!design.use_backdrop_bg} data-testid="td-backdrop-bg"
                  onChange={(e) => updateDesign({ use_backdrop_bg: e.target.checked })}
                  className="accent-[#D4AF37]" />
                <span className="flex flex-col leading-tight">
                  <span>TMDb-Filmposter-Design</span>
                  <span className="text-[9px] text-[#575148]">Backdrop des Films als Ganzseiten-Hintergrund (cinematic)</span>
                </span>
              </label>
            </div>
          </div>

          <div className="card-lux p-4">
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-3 flex items-center gap-2"><ImageIcon size={12} /> Logo</div>
            <LogoUpload url={design.logo_url} onChange={(u) => updateDesign({ logo_url: u })} />
            <div className="mt-3">
              <RangeInput label="Max. Logo-Breite (px)" min={40} max={300} step={5} value={design.logo_max_width} onChange={(v) => updateDesign({ logo_max_width: v })} testid="td-logo-width" />
            </div>
          </div>

          <div className="card-lux p-4">
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-3 flex items-center gap-2"><QrCode size={12} /> QR-Code</div>
            <RangeInput label="QR-Größe (px)" min={80} max={260} step={10} value={design.qr_size} onChange={(v) => updateDesign({ qr_size: v })} testid="td-qr-size" />
            <div className="text-[10px] text-[#575148] mt-2">Native ESC/POS QR — kodiert die Ticket-Nummer</div>
          </div>
        </div>

        {/* ---- Spalte 2 ---- */}
        <div className="card-lux p-4">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">Blöcke · Ziehen zum Umsortieren</div>
            <div className="text-[10px] text-[#575148]">{design.blocks.length} Blöcke</div>
          </div>

          <div className="space-y-1.5" data-testid="td-block-list">
            {design.blocks.map((b, i) => {
              const meta = BLOCK_TYPES.find((t) => t.v === b.type);
              const Icon = meta?.icon || Type;
              const isSel = selectedId === b.id;
              return (
                <React.Fragment key={b.id}>
                  {dragOverIdx === i && <div className="h-0.5 bg-[#D4AF37] rounded" />}
                  <div draggable onDragStart={() => onDragStart(b.id)} onDragOver={(e) => onDragOver(e, i)} onDragLeave={onDragLeave} onDrop={() => onDrop(i)}
                    onClick={() => setSelectedId(b.id)} data-testid={`td-block-${b.id}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded border cursor-pointer transitions-safe select-none ${
                      isSel ? "border-[#D4AF37] bg-[#D4AF37]/[0.06]" : "border-[#1F1F1F] hover:border-[#2A2A2A] bg-[#050505]"
                    }`}>
                    <GripVertical size={13} className="text-[#575148] cursor-grab" />
                    <span className="text-[9px] font-mono text-[#575148] w-6">{i + 1}.</span>
                    <Icon size={13} className="text-[#D4AF37] shrink-0" />
                    <span className="text-[10px] uppercase tracking-widest text-[#EEDC82] w-14 shrink-0">{meta?.l}</span>
                    <span className="text-xs text-[#A8A398] truncate flex-1">
                      {b.type === "text" ? (b.content || "(leer)") : b.type === "logo" ? "· Logo aus URL" : b.type === "qr" ? "· Ticket-QR" : b.type === "divider" ? "· Trennlinie" : "· Abstand"}
                    </span>
                    <div className="flex gap-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => move(b.id, -1)} disabled={i === 0} data-testid={`td-up-${b.id}`} className="p-1 border border-[#2A2A2A] rounded hover:border-[#D4AF37] disabled:opacity-30"><ArrowUp size={11} /></button>
                      <button onClick={() => move(b.id, +1)} disabled={i === design.blocks.length - 1} data-testid={`td-down-${b.id}`} className="p-1 border border-[#2A2A2A] rounded hover:border-[#D4AF37] disabled:opacity-30"><ArrowDown size={11} /></button>
                      <button onClick={() => removeBlock(b.id)} data-testid={`td-del-${b.id}`} className="p-1 border border-[#2A2A2A] rounded hover:border-[#7A1921] hover:text-[#EE9CA1]"><Trash2 size={11} /></button>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
            {dragOverIdx === design.blocks.length && <div className="h-0.5 bg-[#D4AF37] rounded" />}
            <div className="h-2" onDragOver={(e) => onDragOver(e, design.blocks.length)} onDrop={() => onDrop(design.blocks.length)} />
          </div>

          <div className="mt-4 pt-4 border-t border-[#1A1A1A]">
            <div className="text-[10px] uppercase tracking-widest text-[#A8A398] mb-2">Block hinzufügen</div>
            <div className="flex flex-wrap gap-1.5">
              {BLOCK_TYPES.map((t) => (
                <button key={t.v} onClick={() => addBlock(t.v)} data-testid={`td-add-${t.v}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#2A2A2A] hover:border-[#D4AF37] rounded text-[10px] uppercase tracking-widest text-[#A8A398] hover:text-[#D4AF37]">
                  <t.icon size={11} /> <Plus size={10} /> {t.l}
                </button>
              ))}
            </div>
          </div>

          {selected && (
            <div className="mt-5 pt-4 border-t border-[#1A1A1A]" data-testid="td-block-props">
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-3">
                Eigenschaften · {BLOCK_TYPES.find((t) => t.v === selected.type)?.l}
              </div>
              {selected.type === "text" && (
                <div className="space-y-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Text (mit {"{"}Platzhaltern{"}"})</label>
                    <textarea rows={2} value={selected.content || ""} onChange={(e) => updateBlock(selected.id, { content: e.target.value })}
                      data-testid="td-text-content"
                      className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-sm focus:border-[#D4AF37] outline-none font-mono" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#A8A398] mb-1">Platzhalter einfügen</div>
                    <div className="flex flex-wrap gap-1">
                      {PLACEHOLDERS.map((ph) => (
                        <button key={ph} onClick={() => insertPlaceholder(ph)} data-testid={`td-ph-${ph}`}
                          className="px-2 py-0.5 text-[10px] font-mono border border-[#2A2A2A] rounded hover:border-[#D4AF37] text-[#EEDC82]">
                          {"{"}{ph}{"}"}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Schriftgröße</label>
                    <select value={selected.size || "sm"} onChange={(e) => updateBlock(selected.id, { size: e.target.value })}
                      data-testid="td-size"
                      className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-2 py-1.5 text-sm">
                      {SIZE_LABELS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => updateBlock(selected.id, { bold: !selected.bold })} data-testid="td-bold"
                      className={`p-2 border rounded ${selected.bold ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-[#2A2A2A] text-[#A8A398]"}`}><Bold size={14} /></button>
                    <button onClick={() => updateBlock(selected.id, { italic: !selected.italic })} data-testid="td-italic"
                      className={`p-2 border rounded ${selected.italic ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-[#2A2A2A] text-[#A8A398]"}`}><Italic size={14} /></button>
                    <div className="ml-auto text-[10px] uppercase tracking-widest text-[#A8A398] mr-1">Farbe</div>
                    <input type="color" value={selected.color || design.foreground || "#0A0A0A"} data-testid="td-color"
                      onChange={(e) => updateBlock(selected.id, { color: e.target.value })}
                      className="w-10 h-8 rounded bg-transparent border border-[#2A2A2A] cursor-pointer" />
                  </div>
                </div>
              )}
              {selected.type !== "spacer" && (
                <div className="mt-3">
                  <div className="text-[10px] uppercase tracking-widest text-[#A8A398] mb-1">Ausrichtung</div>
                  <div className="grid grid-cols-3 gap-1">
                    {[{ v: "left", Ic: AlignLeft }, { v: "center", Ic: AlignCenter }, { v: "right", Ic: AlignRight }].map(({ v, Ic }) => (
                      <button key={v} onClick={() => updateBlock(selected.id, { align: v })} data-testid={`td-align-${v}`}
                        className={`py-2 flex items-center justify-center border rounded transitions-safe ${
                          (selected.align || "center") === v ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/[0.08]" : "border-[#2A2A2A] text-[#A8A398] hover:border-[#D4AF37]"
                        }`}>
                        <Ic size={14} />
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-3 grid grid-cols-2 gap-2">
                <NumberSmall label="Abstand oben (px)" value={selected.padding_top || 0} onChange={(v) => updateBlock(selected.id, { padding_top: v })} testid="td-pad-top" />
                <NumberSmall label="Abstand unten (px)" value={selected.padding_bottom || 0} onChange={(v) => updateBlock(selected.id, { padding_bottom: v })} testid="td-pad-bottom" />
              </div>
            </div>
          )}
        </div>

        {/* ---- Spalte 3 ---- */}
        <div className="space-y-3 xl:sticky xl:top-4 self-start">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] flex items-center justify-between">
            <span>Live-Vorschau · Beispieldaten</span>
            <span className="text-[#575148] font-mono">{design.paper_width_mm} mm</span>
          </div>
          <div className="rounded p-4" style={{ background: "#181513" }}>
            <div className="mx-auto shadow-2xl" data-testid="td-preview"
              style={{
                width: `${design.paper_width_mm * 3}px`, maxWidth: "100%",
                background: design.background || "#FFFFFF", color: design.foreground || "#0A0A0A",
                padding: "16px 12px", border: design.show_border ? `1px solid ${design.foreground || "#0A0A0A"}20` : "none",
                fontFamily: 'ui-monospace, "SFMono-Regular", Menlo, monospace',
              }}>
              {design.blocks.map((b) => <BlockPreview key={b.id} block={b} design={design} qrValue={SAMPLE_DATA.ticket_no} />)}
            </div>
          </div>
          <div className="text-[10px] text-[#575148] leading-relaxed">
            Beispiel-Bon. Änderungen sind sofort sichtbar. Speichere, damit diese Vorlage bei ausgewählten Vorstellungen verwendet wird.
          </div>
        </div>
      </div>

      {renameOpen && <RenameDialog current={design.name} onClose={() => setRenameOpen(false)} onSave={rename} />}
      {testPrintOpen && <TestPrintDialog design={design} templates={templates} onClose={() => setTestPrintOpen(false)} />}
      {manualPrintOpen && (
        <ManualBlanksDialog
          template={design}
          onClose={() => setManualPrintOpen(false)}
          onSaved={() => { load(); }}
        />
      )}
    </div>
  );
}

// ---- Notfall-Ticket: Designer + Blanko-Bogen drucken ---------------------
const NOTFALL_PRESETS = [
  { key: "vollformular", label: "Vollformular", fields: [
      [{ label: "Filmtitel", hint: "z. B. Interstellar", width_pct: 100 }],
      [{ label: "Datum", hint: "DD.MM.YYYY", width_pct: 50 }, { label: "Uhrzeit", hint: "HH:MM", width_pct: 50 }],
      [{ label: "Saal", hint: "z. B. Saal 1", width_pct: 50 }, { label: "Ticket-Nr.", hint: "manuell vergeben", width_pct: 50 }],
      [{ label: "Reihe", hint: "z. B. C", width_pct: 33 }, { label: "Platz", hint: "z. B. 07", width_pct: 33 }, { label: "Preis", hint: "€", width_pct: 34 }],
    ] },
  { key: "minimal", label: "Minimal", fields: [
      [{ label: "Filmtitel", hint: "", width_pct: 100 }],
      [{ label: "Datum · Uhrzeit", hint: "", width_pct: 60 }, { label: "Sitzplatz", hint: "", width_pct: 40 }],
      [{ label: "Preis", hint: "€", width_pct: 100 }],
    ] },
  { key: "familie", label: "Familienpaket", fields: [
      [{ label: "Filmtitel", hint: "", width_pct: 100 }],
      [{ label: "Datum", hint: "", width_pct: 33 }, { label: "Uhrzeit", hint: "", width_pct: 33 }, { label: "Saal", hint: "", width_pct: 34 }],
      [{ label: "Anzahl Erwachsene", hint: "×", width_pct: 33 }, { label: "Anzahl Kinder", hint: "×", width_pct: 33 }, { label: "Gesamtpreis", hint: "€", width_pct: 34 }],
      [{ label: "Name Familie", hint: "z. B. Müller", width_pct: 100 }],
    ] },
];

function ManualBlanksDialog({ template, onClose, onSaved }) {
  const initial = template?.manual_meta || {};
  const [tab, setTab] = useState("design"); // design | print
  const [fields, setFields] = useState(Array.isArray(initial.fields) && initial.fields.length ? initial.fields : NOTFALL_PRESETS[0].fields);
  const [headerLabel, setHeaderLabel] = useState(initial.header_label || "NOTFALL-TICKET");
  const [cinemaOverride, setCinemaOverride] = useState(initial.cinema_override || "");
  const [sloganOverride, setSloganOverride] = useState(initial.slogan_override || "");
  const [showStub, setShowStub] = useState(initial.show_stub !== false);
  const [showBarcode, setShowBarcode] = useState(initial.show_barcode_hint !== false);
  const [perSheet, setPerSheet] = useState(initial.tickets_per_sheet || 3);
  const [count, setCount] = useState(initial.default_count || 6);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const [previewNonce, setPreviewNonce] = useState(0);

  const previewUrl = useMemo(() =>
    `${process.env.REACT_APP_BACKEND_URL}/api/admin/ticket-templates/${template.id}/manual-blanks/pdf?count=${count}&_=${previewNonce}`,
    [template.id, count, previewNonce]);

  const isDirty =
    JSON.stringify(fields) !== JSON.stringify(initial.fields || []) ||
    headerLabel !== (initial.header_label || "NOTFALL-TICKET") ||
    cinemaOverride !== (initial.cinema_override || "") ||
    sloganOverride !== (initial.slogan_override || "") ||
    showStub !== (initial.show_stub !== false) ||
    showBarcode !== (initial.show_barcode_hint !== false) ||
    Number(perSheet) !== (initial.tickets_per_sheet || 3);

  const saveDesign = async () => {
    setBusy(true);
    try {
      const body = {
        ...template,
        manual_meta: {
          ...(template.manual_meta || {}),
          fields, header_label: headerLabel,
          cinema_override: cinemaOverride, slogan_override: sloganOverride,
          show_stub: showStub, show_barcode_hint: showBarcode,
          tickets_per_sheet: Number(perSheet) || 3,
          default_count: Number(count) || 6,
        },
      };
      delete body.id; delete body._id; delete body.created_at;
      await api.patch(`/admin/ticket-templates/${template.id}`, body);
      toast.success("Notfall-Ticket-Layout gespeichert");
      setSavedAt(Date.now());
      setPreviewNonce((n) => n + 1);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const printNow = async () => {
    setBusy(true);
    try {
      if (isDirty) await saveDesign();
      await api.post(`/admin/ticket-templates/${template.id}/manual-blanks/print-office`, {
        count: Number(count) || 3, print_office: true,
      });
      toast.success(`${count} Blanko-Ticket(s) an Bürodrucker gesendet`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Druck fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const addRow = () => setFields((f) => [...f, [{ label: "Neues Feld", hint: "", width_pct: 100 }]]);
  const removeRow = (ri) => setFields((f) => f.filter((_, i) => i !== ri));
  const moveRow = (ri, dir) => setFields((f) => {
    const arr = [...f]; const j = ri + dir; if (j < 0 || j >= arr.length) return f;
    [arr[ri], arr[j]] = [arr[j], arr[ri]]; return arr;
  });
  const addFieldToRow = (ri) => setFields((f) => {
    const arr = f.map((r) => [...r]);
    arr[ri].push({ label: "Neu", hint: "", width_pct: 50 });
    // Redistribute
    const n = arr[ri].length; const w = Math.floor(100 / n);
    arr[ri] = arr[ri].map((c, i) => ({ ...c, width_pct: i === n - 1 ? 100 - w * (n - 1) : w }));
    return arr;
  });
  const removeFieldFromRow = (ri, ci) => setFields((f) => {
    const arr = f.map((r) => [...r]);
    arr[ri].splice(ci, 1);
    if (arr[ri].length === 0) arr.splice(ri, 1);
    else { const n = arr[ri].length; const w = Math.floor(100 / n); arr[ri] = arr[ri].map((c, i) => ({ ...c, width_pct: i === n - 1 ? 100 - w * (n - 1) : w })); }
    return arr;
  });
  const updateField = (ri, ci, patch) => setFields((f) => {
    const arr = f.map((r) => r.map((c) => ({ ...c })));
    arr[ri][ci] = { ...arr[ri][ci], ...patch };
    return arr;
  });
  const applyPreset = (preset) => {
    if (isDirty && !window.confirm("Ungespeicherte Änderungen verwerfen und Preset laden?")) return;
    setFields(JSON.parse(JSON.stringify(preset.fields)));
  };

  const totalSheets = Math.ceil((Number(count) || 3) / (Number(perSheet) || 3));

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="td-manual-blanks-dialog">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[#1A1A1A] flex items-center gap-3">
          <AlertTriangle size={20} className="text-[#EE9CA1]" />
          <div>
            <div className="font-heading text-xl text-[#EE9CA1]">Notfall-Ticket · Designer</div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398] mt-0.5">Layout gestalten · Handschrift-Zeilen · Preview + Druck</div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <div className="inline-flex rounded border border-[#2A2A2A] overflow-hidden">
              <button onClick={() => setTab("design")} data-testid="notfall-tab-design"
                className={`text-[10px] uppercase tracking-[0.3em] px-4 py-1.5 ${tab === "design" ? "bg-[#D4AF37] text-black" : "text-[#A8A398] hover:text-[#D4AF37]"}`}>
                Design
              </button>
              <button onClick={() => setTab("print")} data-testid="notfall-tab-print"
                className={`text-[10px] uppercase tracking-[0.3em] px-4 py-1.5 border-l border-[#2A2A2A] ${tab === "print" ? "bg-[#D4AF37] text-black" : "text-[#A8A398] hover:text-[#D4AF37]"}`}>
                Drucken
              </button>
            </div>
            <button onClick={onClose}><X size={18} /></button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Left: Designer */}
          <div className="w-1/2 overflow-y-auto p-6 border-r border-[#1A1A1A]">
            {tab === "design" ? (
              <div className="space-y-5">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398] mb-2">Vorlagen laden</div>
                  <div className="flex flex-wrap gap-2">
                    {NOTFALL_PRESETS.map((p) => (
                      <button key={p.key} onClick={() => applyPreset(p)} data-testid={`notfall-preset-${p.key}`}
                        className="text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 rounded border border-[#EEDC82]/40 text-[#EEDC82] hover:bg-[#EEDC82] hover:text-black">
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Header-Label</label>
                    <input value={headerLabel} onChange={(e) => setHeaderLabel(e.target.value)}
                      data-testid="notfall-header-label"
                      className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Tickets pro A4</label>
                    <select value={perSheet} onChange={(e) => setPerSheet(Number(e.target.value))}
                      data-testid="notfall-per-sheet"
                      className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none">
                      <option value={2}>2 · sehr groß</option>
                      <option value={3}>3 · empfohlen</option>
                      <option value={4}>4 · kompakt</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Kino-Name (Override)</label>
                    <input value={cinemaOverride} onChange={(e) => setCinemaOverride(e.target.value)}
                      placeholder="Standard aus Einstellungen"
                      data-testid="notfall-cinema-override"
                      className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Slogan (Override)</label>
                    <input value={sloganOverride} onChange={(e) => setSloganOverride(e.target.value)}
                      placeholder="Standard aus Einstellungen"
                      data-testid="notfall-slogan-override"
                      className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
                  </div>
                </div>

                <div className="flex flex-wrap gap-4 text-xs text-[#A8A398]">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={showStub} onChange={(e) => setShowStub(e.target.checked)}
                      data-testid="notfall-show-stub" />
                    Abriss-Stub rechts
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={showBarcode} onChange={(e) => setShowBarcode(e.target.checked)}
                      data-testid="notfall-show-barcode" />
                    Barcode/QR-Zone unten
                  </label>
                </div>

                <div>
                  <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">Handschrift-Zeilen</div>
                    <button onClick={addRow} data-testid="notfall-add-row"
                      className="text-[10px] uppercase tracking-[0.3em] px-3 py-1 rounded border border-[#8FBC8F]/60 text-[#8FBC8F] hover:bg-[#8FBC8F] hover:text-black inline-flex items-center gap-1">
                      <Plus size={11} /> Zeile
                    </button>
                  </div>
                  <div className="space-y-2">
                    {fields.map((row, ri) => (
                      <div key={ri} className="bg-black/40 border border-[#1A1A1A] rounded p-3" data-testid={`notfall-row-${ri}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-[9px] uppercase tracking-[0.3em] text-[#575148]">Zeile {ri + 1}</span>
                          <div className="ml-auto flex items-center gap-1">
                            <button onClick={() => moveRow(ri, -1)} disabled={ri === 0}
                              className="text-[#A8A398] hover:text-[#D4AF37] disabled:opacity-30 p-1"><ArrowUp size={12} /></button>
                            <button onClick={() => moveRow(ri, 1)} disabled={ri === fields.length - 1}
                              className="text-[#A8A398] hover:text-[#D4AF37] disabled:opacity-30 p-1"><ArrowDown size={12} /></button>
                            <button onClick={() => addFieldToRow(ri)}
                              className="text-[#8FBC8F] hover:text-[#B8E0B8] p-1" title="Feld hinzufügen"><Plus size={12} /></button>
                            <button onClick={() => removeRow(ri)}
                              className="text-[#EE9CA1] hover:text-[#F5B5B9] p-1" title="Zeile löschen"><Trash2 size={12} /></button>
                          </div>
                        </div>
                        <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${row.length}, minmax(0, 1fr))` }}>
                          {row.map((field, ci) => (
                            <div key={ci} className="bg-black/50 border border-[#141414] rounded p-2 space-y-1.5">
                              <input value={field.label} onChange={(e) => updateField(ri, ci, { label: e.target.value })}
                                data-testid={`notfall-field-label-${ri}-${ci}`}
                                placeholder="Label" className="w-full bg-[#050505] border border-[#1A1A1A] rounded px-2 py-1 text-xs focus:border-[#D4AF37] outline-none" />
                              <input value={field.hint || ""} onChange={(e) => updateField(ri, ci, { hint: e.target.value })}
                                placeholder="Hinweis (optional)" className="w-full bg-[#050505] border border-[#1A1A1A] rounded px-2 py-1 text-[10px] text-[#A8A398] focus:border-[#D4AF37] outline-none" />
                              <div className="flex items-center gap-1.5">
                                <input type="number" min={5} max={100} value={field.width_pct || 50}
                                  onChange={(e) => updateField(ri, ci, { width_pct: Math.max(5, Math.min(100, Number(e.target.value) || 50)) })}
                                  className="w-16 bg-[#050505] border border-[#1A1A1A] rounded px-2 py-1 text-[10px] font-mono focus:border-[#D4AF37] outline-none" />
                                <span className="text-[9px] text-[#575148]">% Breite</span>
                                {row.length > 1 && (
                                  <button onClick={() => removeFieldFromRow(ri, ci)}
                                    className="ml-auto text-[#EE9CA1] hover:text-[#F5B5B9]"><X size={10} /></button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                    {fields.length === 0 && (
                      <div className="text-center py-8 text-[#575148] text-xs border border-dashed border-[#2A2A2A] rounded">
                        Keine Zeilen — füge welche hinzu oder wähle eine Vorlage oben.
                      </div>
                    )}
                  </div>
                </div>

                <div className="pt-2 border-t border-[#1A1A1A] flex items-center gap-2">
                  <button onClick={saveDesign} disabled={busy || !isDirty} data-testid="notfall-save"
                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded text-[11px] uppercase tracking-[0.32em] ${isDirty ? "bg-[#D4AF37] hover:bg-[#EEDC82] text-black" : "border border-[#2A2A2A] text-[#575148]"} disabled:opacity-40`}>
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                    {isDirty ? "Layout speichern" : "Gespeichert"}
                  </button>
                  <button onClick={() => setPreviewNonce((n) => n + 1)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#2A2A2A] rounded text-[11px] uppercase tracking-[0.32em] text-[#A8A398] hover:border-[#D4AF37]">
                    <RefreshCw size={12} /> Vorschau aktualisieren
                  </button>
                  {savedAt > 0 && <span className="text-[10px] text-[#8FBC8F]">✓ gespeichert</span>}
                </div>
              </div>
            ) : (
              <div className="space-y-5">
                <div className="text-xs text-[#A8A398] leading-relaxed">
                  Wenn Kasse, Drucker oder Netz ausfällt: druck einen A4-Bogen mit Notfall-Tickets aus,
                  fülle ihn handschriftlich aus und gib den Abriss an den Gast. Den Hauptteil erfasst du später im System nach.
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Anzahl Blanko-Exemplare</label>
                  <input type="number" min={1} max={60} value={count}
                    onChange={(e) => setCount(Math.max(1, Math.min(60, Number(e.target.value) || 1)))}
                    data-testid="td-manual-blanks-count"
                    className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 font-mono text-2xl text-center focus:border-[#EE9CA1] outline-none" />
                  <div className="text-[10px] text-[#575148] mt-1">
                    {perSheet} Tickets pro A4-Blatt · {totalSheets} Blatt / Bogen bei {count} Stück
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openPdf(`/admin/ticket-templates/${template.id}/manual-blanks/pdf?count=${count}`)}
                    data-testid="td-manual-blanks-preview"
                    className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 border border-[#2A2A2A] rounded text-[10px] uppercase tracking-widest text-[#A8A398] hover:border-[#D4AF37] hover:text-[#D4AF37]">
                    <FileText size={12} /> PDF öffnen
                  </button>
                  <button onClick={printNow} disabled={busy} data-testid="td-manual-blanks-submit"
                    className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 bg-[#EE9CA1] hover:bg-[#F5B5B9] text-black rounded text-[10px] uppercase tracking-widest disabled:opacity-40">
                    {busy ? <><Loader2 size={12} className="animate-spin" /> Sende …</> : <><Printer size={12} /> Jetzt drucken</>}
                  </button>
                </div>
                {isDirty && (
                  <div className="text-[10px] text-[#EEDC82] leading-relaxed border border-[#EEDC82]/40 rounded p-2">
                    ⚠️ Ungespeicherte Layout-Änderungen — beim Drucken werden sie automatisch mit gespeichert.
                  </div>
                )}
                <div className="text-[10px] text-[#575148] leading-relaxed">
                  Ausgabegerät: <b className="text-[#EEDC82]">Bürodrucker</b> (Einstellungen → Drucker · A4 IPP/CUPS).
                </div>
              </div>
            )}
          </div>

          {/* Right: Live Preview */}
          <div className="w-1/2 bg-[#050505] p-4 overflow-y-auto">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398] mb-2 flex items-center justify-between">
              <span>Live-Vorschau · A4</span>
              <span className="text-[#575148]">{fields.length} Zeile{fields.length === 1 ? "" : "n"} · {perSheet}/Blatt</span>
            </div>
            <iframe key={previewNonce} title="Notfall-Ticket Vorschau"
              src={previewUrl} data-testid="notfall-preview-iframe"
              className="w-full h-[75vh] bg-white rounded border border-[#1A1A1A]" />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Rename Dialog -------------------------------------------------------
function RenameDialog({ current, onClose, onSave }) {
  const [name, setName] = useState(current || "");
  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="td-rename-dialog">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
          <div className="font-heading text-xl text-[#D4AF37]">Vorlage umbenennen</div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="td-rename-input"
          className="w-full bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-[#2A2A2A] rounded text-[11px] uppercase tracking-widest">Abbrechen</button>
          <button onClick={() => onSave(name)} data-testid="td-rename-save"
            className="px-5 py-2 bg-[#D4AF37] hover:bg-[#EEDC82] text-black rounded text-[11px] uppercase tracking-widest">
            Übernehmen
          </button>
        </div>
        <div className="mt-3 text-[10px] text-[#575148]">Achte darauf, danach oben rechts &bdquo;Änderungen speichern&ldquo; zu klicken.</div>
      </div>
    </div>
  );
}

// ---- Test Print Dialog ---------------------------------------------------
function TestPrintDialog({ design, templates, onClose }) {
  const [conn, setConn] = useState("network");
  const [ip, setIp] = useState("");
  const [port, setPort] = useState(9100);
  const [usbDev, setUsbDev] = useState("/dev/usb/lp0");
  const [cupsName, setCupsName] = useState("");
  const [openDrawer, setOpenDrawer] = useState(false);
  const [tid, setTid] = useState(design.id);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [discovered, setDiscovered] = useState({ usb_devices: [], cups_printers: [] });

  useEffect(() => {
    api.get("/settings").then((r) => {
      setConn(r.data.epson_ticket_connection || "network");
      setIp(r.data.epson_ticket_printer_ip || r.data.card_printer_ip || "");
      setPort(r.data.epson_ticket_printer_port || r.data.card_printer_port || 9100);
      setUsbDev(r.data.epson_ticket_usb_device || "/dev/usb/lp0");
      setCupsName(r.data.epson_ticket_cups_name || "");
    }).catch(() => {});
    api.get("/admin/ticket-printer/discover").then((r) => setDiscovered(r.data)).catch(() => {});
  }, []);

  const submit = async () => {
    setBusy(true); setResult(null);
    try {
      const body = {
        template_id: tid, open_drawer: openDrawer, connection: conn,
        override_ip: conn === "network" ? (ip.trim() || null) : null,
        override_port: conn === "network" ? (Number(port) || null) : null,
        override_usb_device: conn === "usb" ? (usbDev.trim() || null) : null,
        override_cups_name: conn === "cups" ? (cupsName.trim() || null) : null,
      };
      const { data } = await api.post("/admin/ticket-templates/test-print", body);
      setResult({ ok: true, ...data });
      toast.success(`${data.bytes_sent} Bytes gesendet · ${data.connection} → ${data.target}`);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Fehler";
      setResult({ ok: false, msg });
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const canSubmit = conn === "network" ? !!ip.trim() : conn === "usb" ? !!usbDev.trim() : !!cupsName.trim();

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="td-test-dialog">
        <div className="flex items-center gap-2 mb-4">
          <Printer size={18} className="text-[#D4AF37]" />
          <div>
            <div className="font-heading text-xl text-[#D4AF37]">Testdruck · Epson TM-T88VII</div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398] mt-0.5">Netzwerk · USB · CUPS</div>
          </div>
          <button onClick={onClose} className="ml-auto"><X size={18} /></button>
        </div>

        {/* Connection kind tabs */}
        <div className="grid grid-cols-3 gap-1.5 mb-4 p-1 bg-[#050505] border border-[#1A1A1A] rounded">
          {[
            { v: "network", l: "Netzwerk", d: "TCP · 9100" },
            { v: "usb",     l: "USB",      d: "Direkt · /dev/usb/lp0" },
            { v: "cups",    l: "CUPS",     d: "lp -d …" },
          ].map((c) => (
            <button key={c.v} onClick={() => setConn(c.v)} data-testid={`td-conn-${c.v}`}
              className={`px-3 py-2 rounded text-[10px] uppercase tracking-widest transitions-safe ${
                conn === c.v ? "bg-[#D4AF37] text-black" : "text-[#A8A398] hover:text-[#EEDC82]"
              }`}>
              <div className="font-heading text-xs tracking-normal">{c.l}</div>
              <div className="text-[9px] opacity-70 font-mono mt-0.5">{c.d}</div>
            </button>
          ))}
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Vorlage</label>
            <select value={tid} onChange={(e) => setTid(e.target.value)} data-testid="td-test-tid"
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-sm">
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}{t.is_default ? " (Standard)" : ""}</option>)}
            </select>
          </div>

          {conn === "network" && (
            <div className="grid grid-cols-[1fr_100px] gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Drucker-IP</label>
                <input value={ip} onChange={(e) => setIp(e.target.value)} data-testid="td-test-ip"
                  placeholder="192.168.1.50"
                  className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Port</label>
                <input type="number" value={port} onChange={(e) => setPort(Number(e.target.value))} data-testid="td-test-port"
                  className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none" />
              </div>
            </div>
          )}

          {conn === "usb" && (
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">USB-Gerätepfad</label>
              <input value={usbDev} onChange={(e) => setUsbDev(e.target.value)} data-testid="td-test-usb"
                placeholder="/dev/usb/lp0"
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none" />
              {discovered.usb_devices.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-1">
                  <span className="text-[9px] uppercase tracking-widest text-[#575148] mr-1">Gefunden:</span>
                  {discovered.usb_devices.map((d) => (
                    <button key={d} onClick={() => setUsbDev(d)} data-testid={`td-usb-pick-${d}`}
                      className="px-2 py-0.5 text-[10px] font-mono border border-[#2A2A2A] rounded hover:border-[#D4AF37] text-[#EEDC82]">
                      {d}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="mt-2 text-[10px] text-[#575148]">
                  Kein USB-Gerät auf dem Server gefunden. Drucker mit USB an den Server-Rechner anschließen → Linux erkennt ihn als <code className="text-[#EEDC82]">/dev/usb/lp0</code>.
                </div>
              )}
            </div>
          )}

          {conn === "cups" && (
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">CUPS-Druckername</label>
              <input value={cupsName} onChange={(e) => setCupsName(e.target.value)} data-testid="td-test-cups"
                placeholder="Epson_TM_T88VII"
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none" />
              {discovered.cups_printers.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-1">
                  <span className="text-[9px] uppercase tracking-widest text-[#575148] mr-1">Gefunden:</span>
                  {discovered.cups_printers.map((n) => (
                    <button key={n} onClick={() => setCupsName(n)} data-testid={`td-cups-pick-${n}`}
                      className="px-2 py-0.5 text-[10px] font-mono border border-[#2A2A2A] rounded hover:border-[#D4AF37] text-[#EEDC82]">
                      {n}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="mt-2 text-[10px] text-[#575148]">
                  Keine CUPS-Drucker konfiguriert. Unter Linux → System-Einstellungen · Drucker · TM-T88VII hinzufügen · dann Name hier eintragen.
                </div>
              )}
            </div>
          )}

          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input type="checkbox" checked={openDrawer} onChange={(e) => setOpenDrawer(e.target.checked)} data-testid="td-test-drawer" className="accent-[#D4AF37]" />
            Kassenlade nach Druck öffnen
          </label>

          {result && (
            <div className={`rounded p-3 text-xs ${result.ok ? "bg-[#8FBC8F]/10 border border-[#8FBC8F]/40 text-[#B7E1B7]" : "bg-[#C0392B]/10 border border-[#C0392B]/40 text-[#EE9CA1]"}`} data-testid="td-test-result">
              {result.ok
                ? `✓ ${result.bytes_sent} Bytes · ${result.connection} → ${result.target} · Vorlage: ${result.template}`
                : `✗ ${result.msg}`}
            </div>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-[#2A2A2A] rounded text-[11px] uppercase tracking-widest">Schließen</button>
          <button onClick={submit} disabled={busy || !canSubmit} data-testid="td-test-submit"
            className="px-6 py-2 bg-[#D4AF37] hover:bg-[#EEDC82] text-black rounded text-[11px] uppercase tracking-widest disabled:opacity-50 inline-flex items-center gap-2">
            <Printer size={13} /> {busy ? "Sende …" : "Testdruck senden"}
          </button>
        </div>

        <div className="mt-3 text-[10px] text-[#575148] leading-relaxed">
          <div className="text-[#A8A398] mb-1 uppercase tracking-widest font-semibold">Kurz-Hilfe je Verbindung:</div>
          <div><b className="text-[#EEDC82]">Netzwerk:</b> Drucker im LAN, ESC/POS TCP:9100. IP im Router oder am Drucker (Feed-Taste + Ein) prüfen.</div>
          <div><b className="text-[#EEDC82]">USB:</b> Direkt am Server-Rechner. Linux erkennt TM-T88VII als <code>/dev/usb/lp0</code>. Backend braucht Schreibrechte (Gruppe <code>lp</code>).</div>
          <div><b className="text-[#EEDC82]">CUPS:</b> Universelle System-Warteschlange. Vorher &bdquo;TM-T88VII&ldquo; via System-Einstellungen hinzufügen, hier Namen aus <code>lpstat -a</code> eintragen.</div>
        </div>
      </div>
    </div>
  );
}

// ---- Logo-Upload ---------------------------------------------------------
function LogoUpload({ url, onChange }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Bild zu groß (max. 5 MB)");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const full = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      onChange(full);
      toast.success("Logo hochgeladen");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload fehlgeschlagen");
    } finally { setBusy(false); }
  };
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Logo-Bild</label>
      {url && (
        <div className="mt-1 mb-2 flex items-center gap-2 bg-[#050505] border border-[#1F1F1F] rounded p-2" data-testid="td-logo-preview">
          <img src={url} alt="Logo" className="w-16 h-16 object-contain bg-white/95 rounded" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Aktives Logo</div>
            <div className="text-[10px] font-mono text-[#575148] truncate">{url.split("/").pop()?.split("?")[0] || url}</div>
          </div>
          <button onClick={() => onChange("")} data-testid="td-logo-clear"
            className="p-1.5 border border-[#2A2A2A] rounded hover:border-[#7A1921] hover:text-[#EE9CA1]" title="Logo entfernen">
            <X size={12} />
          </button>
        </div>
      )}
      <div className="flex gap-2 mt-1">
        <label className={`flex-1 inline-flex items-center justify-center gap-2 py-2 px-3 rounded border cursor-pointer text-[10px] uppercase tracking-widest transitions-safe ${busy ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10"}`} data-testid="td-logo-upload">
          <ImageIcon size={12} /> {busy ? "Lade hoch …" : (url ? "Logo tauschen" : "Bild hochladen")}
          <input type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/*" className="hidden" disabled={busy}
            onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
      </div>
      <div className="mt-2">
        <label className="text-[10px] uppercase tracking-widest text-[#575148]">oder Externe URL</label>
        <input value={url || ""} onChange={(e) => onChange(e.target.value)} data-testid="td-logo-url"
          placeholder="https://…/logo.png"
          className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-[11px] font-mono focus:border-[#D4AF37] outline-none" />
      </div>
    </div>
  );
}

// ---- Helfer --------------------------------------------------------------
function RangeInput({ label, min, max, step, value, onChange, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">{label}</label>
        <span className="text-xs font-mono text-[#D4AF37]">{value}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} data-testid={testid}
        onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-[#D4AF37] mt-1" />
    </div>
  );
}
function ColorInput({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center gap-2">
      <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-11 h-9 rounded bg-transparent border border-[#2A2A2A] cursor-pointer flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-[#A8A398]">{label}</div>
        <div className="text-xs font-mono text-[#EEDC82]">{value}</div>
      </div>
    </div>
  );
}
function NumberSmall({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">{label}</label>
      <input type="number" min={0} max={80} value={value || 0} data-testid={testid}
        onChange={(e) => onChange(Math.max(0, Math.min(80, Number(e.target.value) || 0)))}
        className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-2 py-1 text-xs focus:border-[#D4AF37] outline-none" />
    </div>
  );
}
